import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Color Picker — live camera view, tap to sample the pixel under the
// crosshair. We use a hidden canvas to copy the current frame and read
// the pixel.

const rgbToHex = (r, g, b) =>
  "#" + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0").toUpperCase()).join("");

const rgbToHsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};

export default function ColorPicker() {
  const [state, setState] = useState("idle");
  const [color, setColor] = useState({ r: 128, g: 128, b: 128, sampled: false });
  const [copied, setCopied] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const containerRef = useRef(null);

  const enable = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setState("unsupported"); return; }
    if (!window.isSecureContext) { setState("insecure"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      setState("ok");
      // V57.6: srcObject attachment moved to post-render useEffect below.
      // The previous (V57.5) approach tried to set srcObject here, but
      // videoRef.current is null when state is still "idle" — the <video>
      // element is conditionally rendered inside {state === "ok" && ...}
      // and hasn't mounted yet. Attempting srcObject = stream silently
      // no-ops on the null ref. Result on iOS Chrome: blank screen
      // after permission grant. The useEffect runs after the re-render
      // when both refs are valid.
    } catch (err) {
      if (err && err.name === "NotAllowedError") setState("denied");
      else if (err && err.name === "NotFoundError") setState("nodevices");
      else setState("denied");
    }
  };

  // V57.6: attach stream after the video element is in the DOM.
  useEffect(() => {
    if (state === "ok" && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [state]);

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const sampleCenter = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || v.readyState < 2) return;
    const w = v.videoWidth, h = v.videoHeight;
    if (!w || !h) return;
    c.width = w; c.height = h;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(v, 0, 0, w, h);
    // Average a 5×5 patch at the center to reduce noise
    const cx = Math.floor(w / 2) - 2;
    const cy = Math.floor(h / 2) - 2;
    const data = ctx.getImageData(cx, cy, 5, 5).data;
    let r = 0, g = 0, b = 0;
    for (let i = 0; i < data.length; i += 4) { r += data[i]; g += data[i + 1]; b += data[i + 2]; }
    const n = data.length / 4;
    setColor({ r: r / n, g: g / n, b: b / n, sampled: true });
  };

  const hex = rgbToHex(color.r, color.g, color.b);
  const [hh, ss, ll] = rgbToHsl(color.r, color.g, color.b);
  const rgbStr = `rgb(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)})`;
  const hslStr = `hsl(${hh}, ${ss}%, ${ll}%)`;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied(""), 1500);
    } catch {}
  };

  return (
    <ToolsShell title="Color Picker">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Aim the crosshair at any color and tap to sample. Reports hex, RGB, and HSL.
          </p>
          <button onClick={enable} style={primaryBtn}>Enable camera</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Camera" />
        </div>
      )}

      {state === "ok" && (
        <>
          <div ref={containerRef} style={{ position: "relative", width: "100%", maxWidth: 540, aspectRatio: "3 / 4", background: "#000", borderRadius: 12, overflow: "hidden", margin: "0 auto" }}>
            <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <canvas ref={canvasRef} style={{ display: "none" }} />
            {/* Crosshair */}
            <div style={{ position: "absolute", top: "50%", left: "50%", width: 40, height: 40, marginLeft: -20, marginTop: -20, border: "2px solid #FFFFFF", borderRadius: "50%", boxShadow: "0 0 6px rgba(0,0,0,0.7)" }} />
            <div style={{ position: "absolute", top: "50%", left: "50%", width: 4, height: 4, marginLeft: -2, marginTop: -2, background: "#FFFFFF", borderRadius: "50%" }} />
          </div>

          <button onClick={sampleCenter} style={{ ...primaryBtn, width: "100%", marginTop: 14 }}>
            Sample color
          </button>

          {color.sampled && (
            <div style={{ marginTop: 18, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: 18, display: "flex", alignItems: "center", gap: 18 }}>
              <div style={{ width: 70, height: 70, borderRadius: 12, background: hex, border: "1px solid #262839", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <Row label="HEX" value={hex} onCopy={() => copy(hex)} copied={copied === hex} />
                <Row label="RGB" value={rgbStr} onCopy={() => copy(rgbStr)} copied={copied === rgbStr} />
                <Row label="HSL" value={hslStr} onCopy={() => copy(hslStr)} copied={copied === hslStr} />
              </div>
            </div>
          )}

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            Colors depend on lighting and white-balance. For exact paint matching, also bring physical samples to a paint store; for design work, take multiple samples in daylight.
          </p>
        </>
      )}
    </ToolsShell>
  );
}

function Row({ label, value, onCopy, copied }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0" }}>
      <div style={{ fontSize: 11, color: "#7C8099", width: 36, fontWeight: 600 }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, color: "#E8E9F3", fontVariantNumeric: "tabular-nums", fontFamily: "monospace" }}>{value}</div>
      <button onClick={onCopy} style={{ background: copied ? "#5BD99A" : "#1C1E2E", color: copied ? "#0A0B14" : "#9A9DBE", border: "none", borderRadius: 5, padding: "3px 8px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
        {copied ? "✓" : "Copy"}
      </button>
    </div>
  );
}
